import { useState, useRef, useLayoutEffect, useEffect, useCallback, useMemo } from 'react'
import { COLOR } from '../../../config/uiTokens'
import { BASE_BUILD_WIGGLE_MS, BASE_BUILD_FLIGHT_MS } from '../../../rules/gameConfig'
import { ensureSlots, basesOf, dropZoneSources } from '../../../rules/gameLogic'
import { subIndex, subCoords, battlefieldColumn, SUB_ROWS } from '../../../rules/subplotGrid'
import { rocketCoverageCells } from '../../../rules/subplotRockets'
import { droneScanCells } from '../../../rules/subplotSurveillance'
import { baseFootprint, baseCenterColumn, canPlaceBase } from '../../../rules/subplotBases'
import { subCenter, hexCorners, pixelToSub, halfEdgeY } from '../../../utils/hexGeometry'
import { playSound } from '../../../utils/soundManager'
import { launchBuildMote } from '../damageMotes'
import { ReconPlanePaths } from '../battleFx'
import { DronePaths } from '../MissileIcon'
import DropZoneMarker, { EGG_FILL, EGG_RIM } from '../DropZoneMarker'
import { R, H, BOARD_W, BOARD_H, ringPoints, plotTileBox, DRONE_RADIUS, SHADOW_DROP, SHADOW_RX, SHADOW_RY, airdropArc, arcCeiling } from './geometry'
import { dropZoneSlots } from './tokens'

// ─── Hex board — Grid assets: rockets, recon planes, Bases, their zones ────────
//
// Everything the square board's PlotStructStrokes / PlotCoverage /
// BoardBuyPlacement / BaseBuildPlacement / BaseOverlays / incomingDrops did,
// on hex geometry. The glyphs are the public SVGs' own paths drawn inline
// (the square board masks them in CSS), so they scale with the board like
// every other mark. Colours are copied from BoardGrid.css and annotated.
//
//   glyphs        RocketGlyph / PlaneGlyph / BaseDroneGlyph / MaturationBar —
//                 used by marks.js's stroke pass on the cells that host them
//   zones         zoneMarks — rocket coverage (19-cell hexagons) per side and
//                 pending recon scans (4-cell), each as a wash inside a
//                 boundary stroke
//   aim prompt    aimBlinkMarks — "choose your target": an attacking
//                 corridor's opp half blinks yellow until it has an aim; a
//                 corridor whose plane still awaits its aim blinks purple
//   buy           buyPlacementCells + buyMarks — the tap-to-place highlights
//                 for a rocket / plane bought from the dock
//   Base build    basePlacementMarks — the corridor's pulsing self half and
//                 the cursor: the brood under construction on the free row
//                 the player is sliding (the funding pair hovers over its
//                 ends — tokens.js)
//   Base ghosts   pendingBaseMarks — this round's builds, tap to take back
//   drop zones    dropZoneTethers — while zones are placed, each pending
//                 zone's arc from the Base that earns it to its landing slot
//   resupply      useResupplyDrops + HexResupplyDrops — each delivery's
//                 crate launched from its Base along that same arc: a fast
//                 rise to the crest, the chute snapping open there, then a
//                 slow swaying float down onto the slot
//   opp build     useBaseBuildFx — the replay's funding drones flying down
//                 to the construction's end cells

// ── Glyphs ──────────────────────────────────────────────────────────────────

// public/rocket_02.svg's path (viewBox 213.46 × 276.03).
const ROCKET_PATH = 'M95.03,276.03v-31.99s-6.2.21-6.2.21H0s.1-14.31.1-14.31l75.79-22.97v-108.91s-53.22-.01-53.22-.01c-.25-1.52-.23-2.74.06-4.1l53.14-16.12v-25.68c.01-2.37.34-4.73.96-7.02,2.38-8.73,5.45-17.01,9.25-25.25C90.38,11.5,97.23.1,106.7,0c8.84-.09,15.4,10,19.66,17.75,4.53,9.12,7.95,18.5,10.59,28.32.58,2.15.84,4.37.83,6.59l-.03,22.63,61.37,18.61c.43,1.39.43,2.6.05,4.13h-61.39s-.02,108.94-.02,108.94l75.7,22.97v14.31s-92.47-.07-92.47-.07v31.78s-25.96.06-25.96.06Z'
const ROCKET_VB = { w: 213.46, h: 276.03 }

// .bg__ps-rocket: light blue #cfe6ff (deployed), --pending #3da5ff, --opp
// #ff8fd6 flipped to point at you; each with a hairline dark outline.
export const ROCKET_FILL = { self: '#cfe6ff', pending: '#3da5ff', opp: '#ff8fd6', buy: '#e6f1ff' }
export function RocketGlyph({ cx, cy, fill, flip = false, scale = 0.76 }) {
    const h = scale * H
    const w = h * (ROCKET_VB.w / ROCKET_VB.h)
    const flipT = flip ? `translate(${cx} ${cy}) scale(1 -1) translate(${-cx} ${-cy})` : undefined
    return (
        <g transform={flipT} style={{ pointerEvents: 'none' }}>
            <svg x={cx - w / 2} y={cy - h / 2} width={w} height={h} viewBox={`0 0 ${ROCKET_VB.w} ${ROCKET_VB.h}`}>
                <path d={ROCKET_PATH} fill={fill} stroke="rgba(0, 0, 0, 0.75)" strokeWidth="6" strokeLinejoin="round" paintOrder="stroke" />
            </svg>
        </g>
    )
}

// .bg__ps-drone: surveillance purple #a855f7 with the same hairline outline.
export const PLANE_FILL = { pending: '#a855f7', buy: '#d8b4fe' }
export function PlaneGlyph({ cx, cy, fill, scale = 0.8 }) {
    const d = scale * H
    return (
        <svg x={cx - d / 2} y={cy - d / 2} width={d} height={d} viewBox="0 0 64 64" style={{ pointerEvents: 'none' }}>
            <ReconPlanePaths fill="rgba(0, 0, 0, 0.75)" stroke="rgba(0, 0, 0, 0.75)" strokeWidth={2.5} />
            <ReconPlanePaths fill={fill} />
        </svg>
    )
}

// A Base IS drones, so it's painted like the drone tiles: the flat veteran
// amber (COLOR.ARMY_PLAYER) for yours with the tile's black glyph, the opp
// tile's red (COLOR.ARMY_OPP) for theirs with a white glyph; a construction
// (and its ghost) wears the new-drone yellow (GRAD_NEW — can't act yet, like
// a fresh deploy) and matures into amber; attacking turns the fill the
// tiles' attack red. No drop shadow — the drone tiles' bevel instead, per
// cell: a thin highlight on the upper-left edges, a thin shadow on the
// lower-right (the tile's inset 2px 2px / -2px -2px pair, HEX_BEVEL).
export const BASE_PAINT = {
    self: { fill: COLOR.ARMY_PLAYER, conFill: COLOR.GRAD_NEW, attackFill: COLOR.GRAD_RED, conOpacity: 1 },
    opp:  { fill: COLOR.ARMY_OPP,    conFill: COLOR.ARMY_OPP, attackFill: COLOR.BTN_ATTACK, conOpacity: 0.55 },
}
export const HEX_BEVEL = { light: 'rgba(255, 255, 255, 0.43)', dark: 'rgba(0, 0, 0, 0.38)', w: 1 }
// The bevel for one cell: its six edges, the upper-left three lit, the
// lower-right three shaded (hexCorners runs right → bottom-right → bottom-left
// → left → top-left → top-right), inset so the strokes sit inside the cell.
export function HexBevel({ cx, cy }) {
    const k = hexCorners(cx, cy, R * (1 - HEX_BEVEL.w / H))
    const edge = (a, b) => `${k[a][0]},${k[a][1]} ${k[b][0]},${k[b][1]}`
    return (
        <g fill="none" strokeWidth={HEX_BEVEL.w} strokeLinecap="round" style={{ pointerEvents: 'none' }}>
            <polyline points={`${edge(2, 3)} ${k[4][0]},${k[4][1]} ${k[5][0]},${k[5][1]}`} stroke={HEX_BEVEL.light} />
            <polyline points={`${edge(5, 0)} ${k[1][0]},${k[1][1]} ${k[2][0]},${k[2][1]}`} stroke={HEX_BEVEL.dark} />
        </g>
    )
}
// The egg on a brood's nest: the airdrop in the making (subplotBases —
// st.egg). One look on both sides: cream over the bar's own colour, a dark
// rim. Its size is the nest cell's.
export const EGG_PATH = 'M32 3 C 47 3, 59 24, 59 41 C 59 55, 47 62, 32 62 C 17 62, 5 55, 5 41 C 5 24, 17 3, 32 3 Z'
export { EGG_FILL, EGG_RIM }
export function EggPaths({ fill = EGG_FILL, stroke = EGG_RIM, strokeWidth = 4 }) {
    return (
        <g>
            <path d={EGG_PATH} fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round" />
            <ellipse cx="24" cy="22" rx="5" ry="8" fill="rgba(255, 255, 255, 0.55)" transform="rotate(-20 24 22)" />
        </g>
    )
}
export function EggGlyph({ cx, cy, scale = 0.62, className }) {
    const d = scale * H
    return (
        <svg x={cx - d / 2} y={cy - d / 2} width={d} height={d} viewBox="0 0 64 64" className={className} style={{ pointerEvents: 'none', overflow: 'visible' }} data-egg="true">
            <EggPaths />
        </svg>
    )
}

// .bg__ps-basedrone: one drone silhouette per live drone of the brood — the
// two on its outer cells, and a hatched egg on the nest (the drones you can
// count ARE the brood's strength). The tile's own glyph colour: black on
// yours, white on theirs.
export const BASE_DRONE_FILL = { self: '#000000', opp: 'rgba(255, 255, 255, 0.92)' }
// BROOD_DRONE_SCALE: the wasp's box as a share of the hex height — large
// enough that its wings nearly reach the hex's edges.
export const BROOD_DRONE_SCALE = 1.02
export function BaseDroneGlyph({ cx, cy, fill, scale = BROOD_DRONE_SCALE, pose = 'spread', opacity }) {
    const d = scale * H
    return (
        <svg x={cx - d / 2} y={cy - d / 2} width={d} height={d} viewBox="0 0 64 64" opacity={opacity} style={{ pointerEvents: 'none' }}>
            <DronePaths fill={fill} pose={pose} />
        </svg>
    )
}

// .bg__ps-basebar: the construction's loading bar — a 1px black frame over a
// dim track, filling in COLOR.BASE_PROGRESS. Sized to the 3-cell bar (its
// centres span 3R; the bar runs a little past them) and placed at the
// footprint's CENTRE OF GRAVITY: the side cells sit half a hex above or
// below the centre cell (odd-q offset), so the bar rides at the mean of
// the three cells' heights, not on the centre cell alone (baseBarCenter).
export const BAR_W = 3.4 * R
export const BAR_H = 7
export function MaturationBar({ cx, cy, progress = 0, centerS = null }) {
    const w = BAR_W, h = BAR_H
    const pct = Math.min(100, 1 + progress * 50)
    return (
        <g style={{ pointerEvents: 'none' }} data-base-bar={centerS ?? undefined}>
            <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} rx="2" fill="rgba(0, 0, 0, 0.18)" stroke="#000000" strokeWidth="1.2" />
            <rect x={cx - w / 2 + 0.6} y={cy - h / 2 + 0.6} width={Math.max(0, (w - 1.2) * pct / 100)} height={h - 1.2} fill={COLOR.BASE_PROGRESS} />
        </g>
    )
}
// Where a Base's bar sits: the mean of its footprint cells' centres.
export function baseBarCenter(side, centerS) {
    const fp = baseFootprint(centerS) ?? [centerS]
    const pts = fp.map(f => subCenter(side, f, R))
    return { x: pts.reduce((a, p) => a + p.x, 0) / pts.length, y: pts.reduce((a, p) => a + p.y, 0) / pts.length }
}

// ── Cell sets ───────────────────────────────────────────────────────────────

// Every self cell that can take a bought rocket / plane: a live structure
// cell with no rocket (deployed or pending) and no pending plane (Broods aren't
// structures and host nothing). Same rule as BattleScreen's freeStructureCellCount.
export function buyPlacementCells(hf, pendingRockets = [], pendingDrones = []) {
    const out = []
    if (!hf?.subplots) return out
    const droneCells = new Set(pendingDrones.map(d => d.launchS))
    hf.subplots.forEach((cell, s) => {
        if (!cell || cell.structureId == null || cell.destroyed) return
        const st = hf.structures?.[cell.structureId]
        if (pendingRockets.includes(s)) return
        if (st?.rocketCells?.includes(s)) return
        if (droneCells.has(s)) return
        out.push(s)
    })
    return out
}

// Per-Base construction progress, keyed by centre cell — the corridor board
// is the source of truth.
export function baseProgressOf(board) {
    const m = {}
    ;(board ?? []).forEach((bf) => {
        for (const base of basesOf(bf)) {
            if (!base.mature && base.centerS != null) m[base.centerS] = base.progress || 0
        }
    })
    return m
}

// Self Base ghosts: centre → { bfId, footprint, undoable }. This round's
// pending builds (tap to take back), plus every DECLARED Base the corridor
// carries (bf.bases) that the Grid doesn't hold yet — after SUBMIT the
// pending record is gone (maturation clears it) and the construction only
// lands on the Grid at resolution, so the ghost stands in through the wait.
export function pendingBaseGhosts(selfBoard, selfHf = null) {
    const m = new Map()
    const onGrid = (centerS) => {
        const cell = selfHf?.subplots?.[centerS]
        return cell?.broodId != null && !!selfHf.broods?.[cell.broodId]
    }
    ;(selfBoard ?? []).forEach((bf) => {
        for (const p of (bf?.pendingBaseBuilds ?? [])) {
            if (p.centerS == null) continue
            m.set(p.centerS, { bfId: bf.id, footprint: baseFootprint(p.centerS) ?? [], undoable: true })
        }
        for (const b of basesOf(bf)) {
            if (b.centerS == null || b.mature || m.has(b.centerS) || onGrid(b.centerS)) continue
            m.set(b.centerS, { bfId: bf.id, footprint: baseFootprint(b.centerS) ?? [], undoable: false })
        }
    })
    return m
}

// ── Zone outlines ───────────────────────────────────────────────────────────

// The edges of a set of cells that face OUT of the set: each cell's six
// edges, kept when the hex just past the edge's midpoint isn't in the set
// (the lattice's notches and the band count as outside). One polyline per
// edge — a boundary drawn cell by cell, the way the square board's per-cell
// inset glows add up to one outline.
export function boundaryEdges(side, cells) {
    const set = cells instanceof Set ? cells : new Set(cells)
    const edges = []
    for (const s of set) {
        const { x: cx, y: cy } = subCenter(side, s, R)
        const corners = hexCorners(cx, cy, R)
        for (let k = 0; k < 6; k++) {
            const [ax, ay] = corners[k], [bx, by] = corners[(k + 1) % 6]
            const mx = (ax + bx) / 2, my = (ay + by) / 2
            const hit = pixelToSub(cx + (mx - cx) * 1.2, cy + (my - cy) * 1.2, R)
            if (hit && hit.side === side && set.has(hit.s)) continue
            edges.push(`${ax},${ay} ${bx},${by}`)
        }
    }
    return edges
}

function zone(key, side, cells, { fill, stroke, strokeW = 1.5, className = 'hexb__zone' }, extra = {}) {
    const arr = [...cells]
    if (!arr.length) return null
    return (
        <g key={key} className={className} style={{ pointerEvents: 'none', ...(extra.style ?? {}) }} {...(extra.attrs ?? {})}>
            {arr.map(s => { const { x, y } = subCenter(side, s, R); return <polygon key={s} points={hexPointsAt(x, y)} fill={fill} /> })}
            <g fill="none" stroke={stroke} strokeWidth={strokeW} strokeLinecap="round">
                {boundaryEdges(side, arr).map((p, i) => <polyline key={i} points={p} />)}
            </g>
        </g>
    )
}
const hexPointsAt = (cx, cy) => hexCorners(cx, cy, R).map(([x, y]) => `${x},${y}`).join(' ')

// Rocket coverage per side (the union of every live / pending rocket's
// 19-cell hexagon — .bg__plotcover blue for yours, the opp rocket's
// bluish-red for theirs) and the recon scans this round's pending planes will
// make (4 cells in surveillance purple, the target ringed).
export const COVER = {
    self: { fill: 'rgba(90, 170, 255, 0.13)', stroke: 'rgba(90, 170, 255, 0.7)' },
    opp:  { fill: 'rgba(158, 58, 112, 0.14)', stroke: 'rgba(158, 58, 112, 0.75)' },
    scan: { fill: 'rgba(168, 85, 247, 0.16)', stroke: '#a855f7' },
}
export function zoneMarks({ selfHf, oppHf, pendingRockets = [], pendingDrones = [], cells }) {
    const out = []
    const coverOf = (hf, pending) => {
        const set = new Set()
        const add = (s) => { for (const c of rocketCoverageCells(s)) set.add(c) }
        pending.forEach(add)
        for (const st of Object.values(hf?.structures ?? {})) {
            for (const s of (st.rocketCells ?? [])) {
                if (hf.subplots?.[s]?.destroyed) continue   // gone with the building
                add(s)
            }
        }
        return set
    }
    out.push(zone('cover-self', 'self', coverOf(selfHf, pendingRockets), COVER.self))
    out.push(zone('cover-opp',  'opp',  coverOf(oppHf, []), COVER.opp))
    for (const [i, d] of pendingDrones.entries()) {
        if (d.targetS == null) continue
        out.push(zone(`scan-${i}`, 'opp', droneScanCells(d.targetS), COVER.scan))
        const c = cells.opp[d.targetS]
        out.push(<polygon key={`scan-c-${i}`} points={ringPoints(c.cx, c.cy, 2)} fill="none" stroke="#a855f7" strokeWidth="2" style={{ pointerEvents: 'none' }} />)
    }
    return out.filter(Boolean)
}

// ── Aim prompt ──────────────────────────────────────────────────────────────
// "Choose your target": each attacking corridor's opp half blinks as one
// faint red wash inside a thin boundary in the attack caret's red
// (COLOR.BTN_ATTACK) until that corridor has an aim cell —
// the square board's .bg__hf-col-blink, on the corridor's own hex outline.
// A corridor whose recon plane still awaits its aim blinks PURPLE instead
// (plane aiming takes the corridor's taps first, so the colour says which aim
// is being picked). Every corridor shares one phase: a negative
// animation-delay of -(now % period) keeps late-armed corridors in step.
export const AIM_BLINK = {
    attack: { fill: 'rgba(224, 80, 80, 0.08)',   stroke: COLOR.BTN_ATTACK,          strokeW: 1.5, className: 'hexb__aim-blink' },
    drone:  { fill: 'rgba(168, 85, 247, 0.10)',  stroke: 'rgba(168, 85, 247, 0.9)',  strokeW: 3, className: 'hexb__aim-blink hexb__aim-blink--drone' },
}
export const AIM_BLINK_PERIOD_MS = 800
export function aimBlinkMarks({ attackAwaitCols, droneAwaitCols, cells, now = Date.now() }) {
    const out = []
    const cols = new Set([...(attackAwaitCols ?? []), ...(droneAwaitCols ?? [])])
    if (!cols.size) return out
    const style = { animationDelay: `${-(now % AIM_BLINK_PERIOD_MS)}ms` }
    for (const col of [...cols].sort((a, b) => a - b)) {
        const kind = droneAwaitCols?.has(col) ? 'drone' : 'attack'
        const corridor = cells.opp.filter(c => battlefieldColumn(c.s) === col).map(c => c.s)
        out.push(zone(`aim-blink-${col}`, 'opp', corridor, AIM_BLINK[kind], { style, attrs: { 'data-aim-blink': `${col}-${kind}` } }))
    }
    return out.filter(Boolean)
}

// ── Crater preview tint ─────────────────────────────────────────────────────
// What the strike's crater preview tints a cell of the opp Hive as: 'known'
// — a structure cell the board shows you (scanned, a rocket on it, or its
// building already hit: the same cells strokeMarks draws for the opp);
// 'base' — a standing Brood or one under construction (always visible); or
// null — plain land, an unknown cell, a crater already, a spent nest.
export function craterPreviewTint(hf, s) {
    const cell = hf?.subplots?.[s]
    if (!cell || cell.destroyed) return null
    const brood = cell.broodId != null ? hf.broods?.[cell.broodId] : null
    if (brood) return brood.spent ? null : 'base'
    if (cell.structureId == null) return null
    const st = hf.structures?.[cell.structureId]
    if (!st) return null
    const hit = (st.footprint ?? []).some(f => hf.subplots[f]?.destroyed)
    return cell.scanned || hit || st.rocketCells?.includes(s) ? 'known' : null
}

// ── Buy placement (rocket / plane from the dock) ────────────────────────────
// .bg__rkplace-cell: every eligible cell pulses in the asset's colour with
// its glyph; the board routes a tap on one to onBuyPlace(s) and any other
// tap to onBuyCancel.
export function buyMarks({ mode, cells, geo }) {
    const drone = mode === 'drone'
    const fill = drone ? 'rgba(168, 85, 247, 0.24)' : 'rgba(63, 134, 214, 0.28)'
    const stroke = drone ? '#a855f7' : '#3f86d6'
    return cells.map((s) => {
        const c = geo.self[s]
        return (
            <g key={s} className="hexb__buy" data-buy-s={s} style={{ pointerEvents: 'none' }}>
                <polygon points={c.pts} fill={fill} />
                <polygon points={ringPoints(c.cx, c.cy, 2)} fill="none" stroke={stroke} strokeWidth="2" />
                {drone ? <PlaneGlyph cx={c.cx} cy={c.cy} fill={PLANE_FILL.buy} scale={0.7} />
                       : <RocketGlyph cx={c.cx} cy={c.cy} fill={ROCKET_FILL.buy} scale={0.66} />}
            </g>
        )
    })
}

// ── Build Base placement ────────────────────────────────────────────────────
// The corridor's whole self half wears the pulsing amber border on its hex
// boundary (.bg__baseplace-band); the full 3-cell footprint previews at the hovered /
// tapped row — yellow where it can land,
// red where it can't (structure, crater, Port, a build already pending).
export function basePlaceable(selfHf, selfBoard, col, centerS) {
    if (centerS == null || !canPlaceBase(selfHf, col, centerS)) return false
    const blocked = new Set((selfBoard?.[col]?.pendingBaseBuilds ?? []).flatMap(pb => baseFootprint(pb.centerS) ?? []))
    return !(baseFootprint(centerS) ?? []).some(c => blocked.has(c))
}
// The rows (sr) of corridor `col` where a brood can stand right now: its
// centre column's rows whose whole bar is free land, clear of this round's
// other builds. The placement cursor only ever stands on one of these.
export function basePlaceableRows(selfHf, selfBoard, col) {
    const sc = baseCenterColumn(col)
    const out = []
    for (let sr = 0; sr < SUB_ROWS; sr++) if (basePlaceable(selfHf, selfBoard, col, subIndex(sc, sr))) out.push(sr)
    return out
}
// The free row nearest a (fractional) target row; a tie goes the way the
// finger is travelling (dir < 0: toward the band).
export function nearestBaseRow(rows, target, dir = 0) {
    let best = null
    for (const r of rows) {
        if (best == null) { best = r; continue }
        const d = Math.abs(r - target), b = Math.abs(best - target)
        const tie = Math.abs(d - b) < 1e-6
        if ((!tie && d < b) || (tie && dir !== 0 && Math.sign(r - best) === Math.sign(dir))) best = r
    }
    return best
}
// The row a self-half point picks — anywhere across the half, not just
// inside the corridor (the bar spans the corridor, so the row is the whole
// choice): the row of the hex under it, or, where no hex is (a notch of
// the front edge), the centre column's row level with it. Null above the
// self half.
export function baseRowAt(col, x, y) {
    const edge = halfEdgeY('self', R)
    if (y < edge) return null
    const hit = pixelToSub(x, y, R)
    if (hit?.side === 'self') return subCoords(hit.s).sr
    const odd = baseCenterColumn(col) & 1
    const sr = Math.round((y - edge - H / 2 - (odd ? H / 2 : 0)) / H)
    return Math.max(0, Math.min(SUB_ROWS - 1, sr))
}
export const baseCenterAtRow = (col, sr) => (sr == null ? null : subIndex(baseCenterColumn(col), sr))

// The slide cue: a column of thick chevrons above the cursor and one below,
// each running outward in turn like a slide-to-unlock sweep — the chevrons
// light one after another away from the brood, drifting that way as they
// fade (.hexb__slide-chevron) — up, then down, to say "slide it either
// way". A column shows only while a free row lies that way. The cue rides
// the cursor (a CSS transform transition) so it glides row to row with it.
const CHEVRON_W    = 2 * R
const CHEVRON_H    = 0.5 * H
const CHEVRON_STEP = 0.45 * H     // from one chevron to the next, outward
const CHEVRON_GAP  = 0.52 * H     // from the centre cell's centre to the first chevron
const CHEVRON_COUNT = 3
export const SLIDE_CYCLE_MS = 2000
const CHEVRON_STAGGER_MS = 130
const chevronPoints = (dir, k) => {
    const base = dir * (CHEVRON_GAP + k * CHEVRON_STEP)
    const tip = base + dir * CHEVRON_H
    return `${-CHEVRON_W / 2},${base} 0,${tip} ${CHEVRON_W / 2},${base}`
}
export function baseSlideArrows({ cursor, rows }) {
    if (cursor == null) return null
    const { sr } = subCoords(cursor)
    const up = rows.some(r => r < sr), down = rows.some(r => r > sr)
    if (!up && !down) return null
    const { x, y } = subCenter('self', cursor, R)
    const column = (name, dir, startMs) => (
        <g className={`hexb__slide-arrow hexb__slide-arrow--${name}`} data-slide-arrow={name}>
            {Array.from({ length: CHEVRON_COUNT }, (_, k) => (
                <g key={k} className="hexb__slide-chevron" data-slide-chevron={k}
                    style={{ animationDelay: `${startMs + k * CHEVRON_STAGGER_MS}ms`, animationDuration: `${SLIDE_CYCLE_MS}ms` }}>
                    <polyline points={chevronPoints(dir, k)} fill="none" stroke="#111111" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points={chevronPoints(dir, k)} fill="none" stroke={PAIR_STROKE} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                </g>
            ))}
        </g>
    )
    return (
        <g key="slide-arrows" data-slide-arrows={cursor} className="hexb__slide-arrows"
            style={{ transform: `translate(${x}px, ${y}px)`, pointerEvents: 'none' }}>
            {up && column('up', -1, 0)}
            {down && column('down', 1, SLIDE_CYCLE_MS / 2)}
        </g>
    )
}

export function basePlacementMarks({ col, cursor, rows = [], geo }) {
    const out = []
    // The band follows the corridor's own hex boundary (boundaryEdges — the
    // aim blink's outline on the opp half), not a box around it.
    const corridor = geo.self.filter(c => battlefieldColumn(c.s) === col).map(c => c.s)
    out.push(
        <g key="band" className="hexb__baseband" data-base-band={col} fill="none" stroke="rgba(212, 134, 10, 1)" strokeWidth="2"
            strokeLinecap="round" style={{ pointerEvents: 'none' }}>
            {boundaryEdges('self', corridor).map((p, i) => <polyline key={i} points={p} />)}
        </g>
    )
    // The cursor: the brood as it would rise — the construction's paint and
    // bevel on its three cells and its empty maturation bar (a pending
    // ghost's look). Always on a free row, so it never shows red.
    if (cursor != null) {
        for (const s of (baseFootprint(cursor) ?? [])) {
            const c = geo.self[s]
            out.push(
                <g key={`fp-${s}`} data-base-preview="ok" data-base-cursor={cursor} style={{ pointerEvents: 'none' }}>
                    <polygon points={c.pts} fill={BASE_PAINT.self.conFill} />
                    <HexBevel cx={c.cx} cy={c.cy} />
                </g>
            )
        }
        const { x, y } = baseBarCenter('self', cursor)
        out.push(<MaturationBar key="cursor-bar" cx={x} cy={y} progress={0} centerS={cursor} />)
        out.push(baseSlideArrows({ cursor, rows }))
    }
    return out
}

// ── Pending Base ghosts ─────────────────────────────────────────────────────
// .bg__basecell--pending: a build not yet on the Grid as a yellow bar on its
// cells — the construction's own paint and bevel. This round's builds wear
// an X (HexBoard's BroodX) that undoes them until SUBMIT. Its maturation bar (at zero) is drawn by ghostBarMarks
// in the bar layer above the strokes.
export function pendingBaseMarks({ ghosts, geo }) {
    const out = []
    for (const { footprint } of ghosts.values()) {
        for (const s of footprint) {
            const c = geo.self[s]
            out.push(
                <g key={`ghost-${s}`} data-ghost-s={s} style={{ pointerEvents: 'none' }}>
                    <polygon points={c.pts} fill={BASE_PAINT.self.conFill} />
                    <HexBevel cx={c.cx} cy={c.cy} />
                </g>
            )
        }
    }
    return out
}

// The ghosts' maturation bars — 1% at build — in the bar layer.
export function ghostBarMarks({ ghosts }) {
    return [...ghosts.keys()].map((centerS) => {
        const { x, y } = baseBarCenter('self', centerS)
        return <MaturationBar key={`ghostbar-${centerS}`} cx={x} cy={y} progress={0} centerS={centerS} />
    })
}

// ── Drop-zone tethers ───────────────────────────────────────────────────────
// Every mature Base earns a drop zone, and its airdrop launches FROM that
// Base (gameLogic.dropZoneSources pairs each zone with the nearest
// unassigned mature Base). While the zones are placed, each pending marker
// wears its tether: the arc (geometry.airdropArc) from its Base's bar
// centre to the slot the marker holds (tokens.dropZoneSlots — the same slot
// the marker is drawn on), a dashed yellow line over a dark under-stroke so
// it reads on tan land and lane edges alike, dashes crawling Base → zone
// (.hexb__tether). A placement plus under the mouse previews the tether it
// would get at half strength (hoverCol), so the choice is seen before the
// click. A zone with no Base left to feed it (the Base died) gets none.
export const TETHER_STROKE = 'rgba(240, 224, 64, 0.95)'      // COLOR.DROP_ZONE
export const TETHER_HALO   = 'rgba(0, 0, 0, 0.45)'
const TETHER_W = 2
const TETHER_DASH = '6 8'
export function dropZoneTethers({ selfBoard, hostingCaps, dropZones = [], canDeploy = false, placing = false, hoverCol = null }) {
    if (!selfBoard?.length) return []
    const zonesBefore = (i) => dropZones.slice(0, i).filter(z => z === dropZones[i]).length
    const slotsOf = new Map()
    const slots = (c) => {
        if (!slotsOf.has(c)) {
            const pending = dropZones.filter(z => z === c).length
            slotsOf.set(c, dropZoneSlots(selfBoard[c], hostingCaps?.[c], { canDeploy, pending, placing }))
        }
        return slotsOf.get(c)
    }
    const tether = (key, src, c, p, opacity, extra = {}) => {
        if (!src || p == null) return null
        const from = subCenter('self', src.centerS, R)          // the nest: the egg's own cell
        const box = plotTileBox('self', c, p)
        const arc = airdropArc(from, { x: box.cx, y: box.cy }, { ceilingY: arcCeiling('self') })
        return (
            <g key={key} data-dz-tether={key} data-dz-from={src.centerS} data-dz-col={c} data-dz-slot={p} opacity={opacity} style={{ pointerEvents: 'none' }} {...extra}>
                <path d={arc.path} fill="none" stroke={TETHER_HALO} strokeWidth={TETHER_W + 2.5} strokeLinecap="round" />
                <path className="hexb__tether" d={arc.path} fill="none" stroke={TETHER_STROKE} strokeWidth={TETHER_W}
                    strokeDasharray={TETHER_DASH} strokeLinecap="round" />
                <circle cx={from.x} cy={from.y} r={2.4} fill={TETHER_STROKE} stroke={TETHER_HALO} strokeWidth="1" />
            </g>
        )
    }
    const out = []
    const sources = dropZoneSources(selfBoard, dropZones)
    dropZones.forEach((c, i) => {
        const p = slots(c).zones[zonesBefore(i)] ?? null
        const el = tether(i, sources[i], c, p, 1)
        if (el) out.push(el)
    })
    if (placing && hoverCol != null && selfBoard[hoverCol]) {
        const src = dropZoneSources(selfBoard, [...dropZones, hoverCol])[dropZones.length]
        const el = tether('hover', src, hoverCol, slots(hoverCol).place, 0.45, { 'data-dz-hover': hoverCol })
        if (el) out.push(el)
    }
    return out
}

// ── Resupply arrivals ───────────────────────────────────────────────────────
// Declared drop-zone deliveries: one parachute crate per delivered drone,
// launched from the Base that earned the zone (resupplyDeliveries.sources —
// dropZoneSources on the pre-battle board, aligned with the delivered
// columns; useGameState) and flown along the tether's arc onto the slot the
// fresh green token claimed (diffed by token uid against the previous
// board; several deliveries into one corridor take its new tokens front to
// back, in delivery order). Consumed once per deliveries object so later
// board changes can't re-fire. While a crate is in flight its tile stays
// hidden, then fades in on the ground as the crate lands.
//
// The flight has two beats on one arc: the LAUNCH — the crate shoots out
// of the Base's centre and climbs to the crest, fast and easing off
// (DZ_LAUNCH_MS) — and, the chute snapping open at the crest (its sound,
// the icon popping to full size), the FLOAT: the slow leaf-like descent
// down the far half of the arc onto the slot, swaying (DZ_FLOAT_MS). Its
// shadow runs the straight ground track beneath. A crate with no Base to
// launch from (the zone's Base died) lobs in from its side's rear edge.
export const DZ_LAUNCH_MS = 650
export const DZ_FLOAT_MS  = 2300
export const DZ_FLIGHT_MS = DZ_LAUNCH_MS + DZ_FLOAT_MS
const DZ_STAGGER_MS = 420
const DZ_CHUTE_SOUND_GAP_MS = 250
// Where a drop's crate launches and lands, in board units: from the nest
// cell of the brood that laid it (the egg's own centre), onto the slot.
export function airdropEndpoints(side, c, p, fromS = null) {
    const box = plotTileBox(side, c, p)
    const to = { x: box.cx, y: box.cy }
    const from = fromS != null
        ? subCenter(side, fromS, R)
        : { x: box.cx, y: side === 'opp' ? -8 : BOARD_H + 8 }
    return { from, to }
}
export function useResupplyDrops({ selfBoard, oppBoard, isAnimating, resupplyDeliveries }) {
    const [drops, setDrops] = useState([])
    const [landed, setLanded] = useState(() => new Set())
    const prevSelfRef = useRef(selfBoard)
    const prevOppRef = useRef(oppBoard)
    const consumedRef = useRef(null)
    const idRef = useRef(0)
    const lastChuteRef = useRef(0)

    useLayoutEffect(() => {
        const prevSelf = prevSelfRef.current; prevSelfRef.current = selfBoard
        const prevOpp = prevOppRef.current;   prevOppRef.current = oppBoard
        if (isAnimating || !selfBoard?.length) return
        if (!resupplyDeliveries || consumedRef.current === resupplyDeliveries) return
        const queue = []
        const diff = (board, prev, cols, sources, side) => {
            if (!prev?.length || !cols?.length) return false
            let fired = false
            const claimed = new Set()
            cols.forEach((c, i) => {
                const bf0 = board[c], pbf0 = prev[c]
                if (!bf0 || !pbf0 || (bf0.newArmies || 0) <= (pbf0.newArmies || 0)) return
                const bf = ensureSlots(bf0)
                const prevUids = new Set(ensureSlots(pbf0).slots.filter(Boolean).map(t => t.uid))
                const p = bf.slots.findIndex((t, j) => t && t.kind === 'green' && t.delivered && !prevUids.has(t.uid) && !claimed.has(`${c}-${j}`))
                if (p === -1) return
                claimed.add(`${c}-${p}`)
                queue.push({ id: ++idRef.current, side, c, p, fromS: sources?.[i]?.centerS ?? null, delay: queue.length * DZ_STAGGER_MS })
                fired = true
            })
            return fired
        }
        const firedSelf = diff(selfBoard, prevSelf, resupplyDeliveries.self, resupplyDeliveries.sources?.self, 'self')
        const firedOpp = diff(oppBoard, prevOpp, resupplyDeliveries.opp, resupplyDeliveries.sources?.opp, 'opp')
        if (firedSelf || firedOpp) consumedRef.current = resupplyDeliveries
        if (!queue.length) return
        setDrops(cur => [...cur, ...queue])
        const now = performance.now()
        queue.forEach(d => {
            setTimeout(() => playSound('resupply'), d.delay + DZ_FLIGHT_MS - 100)
            // The chute opens at the crest.
            const at = Math.max(now + d.delay + DZ_LAUNCH_MS, lastChuteRef.current + DZ_CHUTE_SOUND_GAP_MS)
            lastChuteRef.current = at
            setTimeout(() => playSound('parachute', { offset: 0.2, duration: 1.5 }), at - now)
        })
    }, [selfBoard, oppBoard, isAnimating, resupplyDeliveries])

    const onLanded = useCallback((drop) => {
        const key = `${drop.side}-${drop.c}-${drop.p}`
        setDrops(cur => cur.filter(d => d.id !== drop.id))
        setLanded(cur => new Set(cur).add(key))
        setTimeout(() => setLanded(cur => { const n = new Set(cur); n.delete(key); return n }), 700)
    }, [])

    const hidden = useMemo(() => new Set(drops.map(d => `${d.side}-${d.c}-${d.p}`)), [drops])
    return { drops, onLanded, hidden, landed }
}

// The arc, sampled for the Web Animations keyframes: the launch half on an
// ease-out (fast off the Base, slowing into the crest), the float half at
// a leaf's steady pace. Offsets are the timeline's share.
const LAUNCH_SAMPLES = 10
const FLOAT_SAMPLES  = 14
const easeOut = (u) => 1 - (1 - u) * (1 - u) * (1 - u)
export function airdropTimeline(arc) {
    const total = DZ_FLIGHT_MS
    const pts = []
    for (let i = 0; i <= LAUNCH_SAMPLES; i++) {
        const u = i / LAUNCH_SAMPLES
        pts.push({ t: 0.5 * easeOut(u), offset: (u * DZ_LAUNCH_MS) / total })
    }
    for (let i = 1; i <= FLOAT_SAMPLES; i++) {
        const u = i / FLOAT_SAMPLES
        pts.push({ t: 0.5 + 0.5 * u, offset: (DZ_LAUNCH_MS + u * DZ_FLOAT_MS) / total })
    }
    const track = (t) => ({ x: arc.from.x + (arc.to.x - arc.from.x) * t, y: arc.from.y + (arc.to.y - arc.from.y) * t })
    return pts.map(({ t, offset }) => ({ offset, air: arc.at(t), ground: track(t) }))
}

// The crates themselves. Each flies its own arc in board units scaled to
// the board's current pixels: the wrapper is translated along the sampled
// air path — it leaves the nest AS THE EGG, and at the crest the chute
// snaps open over it (the egg fades into the parachute crate, which pops to
// full size) and sways from there — the shadow along the ground track.
// jsdom has no Element.animate — then the crate simply lands when its time
// is up.
const CRATE_SIZE = 44                  // px at the reference width
const EGG_SIZE   = 26
const SHADOW_W = 16, SHADOW_H = 9
const SWAY_MS = 900
function AirdropCrate({ drop, onLanded, boardRef }) {
    const elRef = useRef(null)
    const shadowRef = useRef(null)
    const iconRef = useRef(null)
    const eggRef = useRef(null)
    const chuteRef = useRef(null)
    const swayRef = useRef(null)
    const landedRef = useRef(onLanded)
    useEffect(() => { landedRef.current = onLanded }, [onLanded])
    useLayoutEffect(() => {
        const el = elRef.current, shadow = shadowRef.current, icon = iconRef.current, sway = swayRef.current
        const egg = eggRef.current, chute = chuteRef.current
        const board = boardRef?.current
        const scale = (board?.clientWidth || 0) / BOARD_W || 1
        const { from, to } = airdropEndpoints(drop.side, drop.c, drop.p, drop.fromS)
        const arc = airdropArc(from, to, { ceilingY: arcCeiling(drop.side) })
        const frames = airdropTimeline(arc)
        const px = (p) => `translate(${(p.x * scale).toFixed(2)}px, ${(p.y * scale).toFixed(2)}px)`
        const svg = chute?.querySelector('svg')
        if (svg) svg.style.fontSize = `${Math.round(CRATE_SIZE * scale)}px`
        if (egg) { egg.style.width = `${EGG_SIZE * scale}px`; egg.style.height = `${EGG_SIZE * scale}px` }
        if (shadow) { shadow.style.width = `${SHADOW_W * scale}px`; shadow.style.height = `${SHADOW_H * scale}px` }
        const done = () => landedRef.current?.(drop)
        if (!el || typeof el.animate !== 'function') {
            const t = setTimeout(done, drop.delay + DZ_FLIGHT_MS)
            return () => clearTimeout(t)
        }
        const launchFrac = DZ_LAUNCH_MS / DZ_FLIGHT_MS
        const opts = { duration: DZ_FLIGHT_MS, delay: drop.delay, fill: 'both', easing: 'linear' }
        const flight = el.animate(frames.map(f => ({ offset: f.offset, transform: px(f.air) })), opts)
        flight.onfinish = done
        if (shadow?.animate) {
            shadow.animate(frames.map(f => ({ offset: f.offset, transform: `${px(f.ground)} translate(-50%, -50%)`, opacity: f.offset < launchFrac ? 0.15 + 0.2 * (f.offset / launchFrac) : 0.35 + 0.25 * ((f.offset - launchFrac) / (1 - launchFrac)) })), opts)
        }
        if (icon?.animate) {
            // Off the nest at egg size; the chute snaps open at the crest.
            icon.animate([
                { offset: 0, transform: 'translate(-50%, -50%) scale(1)' },
                { offset: launchFrac * 0.9, transform: 'translate(-50%, -50%) scale(1)' },
                { offset: launchFrac + 0.03, transform: 'translate(-50%, -50%) scale(1.12)' },
                { offset: launchFrac + 0.08, transform: 'translate(-50%, -50%) scale(1)' },
                { offset: 1, transform: 'translate(-50%, -50%) scale(1)' },
            ], opts)
        }
        // The egg is the thing in the air until the crest; the crate takes over there.
        if (egg?.animate) {
            egg.animate([
                { offset: 0, opacity: 1 }, { offset: launchFrac, opacity: 1 }, { offset: launchFrac + 0.06, opacity: 0 }, { offset: 1, opacity: 0 },
            ], opts)
        }
        if (chute?.animate) {
            chute.animate([
                { offset: 0, opacity: 0 }, { offset: launchFrac, opacity: 0 }, { offset: launchFrac + 0.04, opacity: 1 }, { offset: 1, opacity: 1 },
            ], opts)
        }
        if (sway?.animate) {
            sway.animate([
                { transform: `translateX(${-7 * scale}px) rotate(-9deg)` },
                { transform: `translateX(${7 * scale}px) rotate(9deg)` },
                { transform: `translateX(${-7 * scale}px) rotate(-9deg)` },
            ], { duration: SWAY_MS, delay: drop.delay + DZ_LAUNCH_MS, iterations: Infinity, easing: 'ease-in-out' })
        }
        return () => { try { flight.cancel() } catch (_) { /* gone */ } }
    }, [drop, boardRef])
    return (
        <>
            <div ref={shadowRef} className="bg__dz-drop-shadow" data-drop-shadow={`${drop.side}-${drop.c}-${drop.p}`} />
            <div ref={elRef} className="bg__dz-drop" data-drop={`${drop.side}-${drop.c}-${drop.p}`} data-drop-from={drop.fromS ?? undefined}>
                <div ref={swayRef} className="bg__dz-drop-sway">
                    <div ref={iconRef} className="bg__dz-drop-icon">
                        <div ref={chuteRef} className="bg__dz-drop-chute">
                            <DropZoneMarker
                                size={CRATE_SIZE}
                                color={drop.side === 'opp' ? COLOR.GRAD_RED : COLOR.GRAD_NEW}
                                style={{ stroke: '#000000', strokeWidth: 50, paintOrder: 'stroke' }}
                            />
                        </div>
                        <svg ref={eggRef} className="bg__dz-drop-egg" viewBox="0 0 64 64" data-drop-egg="true" style={{ overflow: 'visible' }}>
                            <EggPaths strokeWidth={5} />
                        </svg>
                    </div>
                </div>
            </div>
        </>
    )
}
export function HexResupplyDrops({ drops, onLanded, boardRef }) {
    if (!drops?.length) return null
    return (
        <div className="bg__dz-drops">
            {drops.map(d => <AirdropCrate key={d.id} drop={d} onLanded={onLanded} boardRef={boardRef} />)}
        </div>
    )
}

// The trail a flying crate leaves along its arc, in the SVG beneath it — a
// faint dashed echo of the tether that fades as the crate comes down.
export function airdropTrails(drops) {
    return (drops ?? []).map(d => {
        const { from, to } = airdropEndpoints(d.side, d.c, d.p, d.fromS)
        const arc = airdropArc(from, to, { ceilingY: arcCeiling(d.side) })
        return (
            <path key={d.id} className="hexb__airdrop-trail" data-airdrop-trail={`${d.side}-${d.c}-${d.p}`}
                d={arc.path} fill="none" stroke={d.side === 'opp' ? COLOR.GRAD_RED : TETHER_STROKE} strokeWidth="1.5"
                strokeDasharray="4 6" strokeLinecap="round"
                style={{ '--trail-ms': `${d.delay + DZ_FLIGHT_MS}ms`, animationDelay: `${d.delay}ms`, pointerEvents: 'none' }} />
        )
    })
}

// ── The Base build flight ───────────────────────────────────────────────────
// The two funding drones leave their slots and descend onto the pad: each
// tile (data-token-key) is cloned and flown down to one end cell of the
// construction rising on that side's Grid — the two cells the pair lands on
// (padCells). Fired the frame BEFORE the board removes the tiles, so the
// clones are already covering them. The opp replay fires it from
// useBaseBuildFx; a self build fires it as the placement tap commits.
export const padCells = (centerS) => {
    const fp = baseFootprint(centerS)
    return fp ? [fp[1], fp[2]] : null
}
export function launchBaseBuildFlight({ side, col, uids, centerS, container, svg }) {
    if (!container || !svg) return
    const rect = svg.getBoundingClientRect()
    if (!rect.width) return
    const pads = padCells(centerS)
    if (!pads) return
    const scale = rect.width / BOARD_W
    ;(uids ?? []).slice(0, 2).forEach((uid, i) => {
        const { x, y } = subCenter(side, pads[i], R)
        launchBuildMote({
            fromEl: container.querySelector(`[data-token-key="${side}-${col}-${uid}"]`),
            toPoint: { x: rect.left + x * scale, y: rect.top + y * scale },
            wiggleMs: BASE_BUILD_WIGGLE_MS,
            flightMs: BASE_BUILD_FLIGHT_MS,
            targetW: 2 * R * scale,
            // The tile's ground shadow, as the board casts it, rides down with it.
            shadow: { dropPx: SHADOW_DROP * scale, rx: SHADOW_RX * scale, ry: SHADOW_RY * scale },
        })
    })
}

// ── Opp replay: the Base build beat ─────────────────────────────────────────
// useGameState fires baseBuildFx { col, builds: [{ centerS, uids }], key }
// the frame before the replay board removes the two funding drones.
export function useBaseBuildFx({ baseBuildFx, containerRef, svgRef }) {
    useEffect(() => {
        if (!baseBuildFx || !(baseBuildFx.builds ?? []).length) return
        for (const build of baseBuildFx.builds) {
            launchBaseBuildFlight({
                side: 'opp', col: baseBuildFx.col, uids: build?.uids, centerS: build?.centerS,
                container: containerRef.current, svg: svgRef.current,
            })
        }
    }, [baseBuildFx])  // eslint-disable-line react-hooks/exhaustive-deps
}

// ── The funding pair ────────────────────────────────────────────────────────
// The cue that a corridor can build is its two veterans; the build reads as
// those two becoming the Base. pairMarks draws, beneath the tiles, a halo
// on each of the pair's drones (a ring PAIR_PAD outside the tile, wide
// enough to show past a lifted tile and the one dragged over it) and a link
// between the two — the tie that says "these two", all in the construction's
// yellow (the Base they are about to become). A drag that is hovering a mate
// shows only the mate's ring (the dragged tile is under the finger); the
// placement step shows the whole tie, pulsing with the band (.hexb__pair),
// (the hovered mate's ring also ripples outward, so the catch reads past
// the finger covering it)
// and it holds until the spot is picked or the build is cancelled.
export const PAIR_STROKE = 'rgba(240, 224, 64, 0.95)'     // the construction's yellow (COLOR.GRAD_NEW)
const PAIR_FILL = 'rgba(240, 224, 64, 0.16)'
const PAIR_RING_W = 2.5
const PAIR_PAD = 6
export function pairMarks({ col, uids, slotOf, link = true }) {
    const centres = (uids ?? []).map(uid => {
        const p = slotOf(uid)
        return p == null ? null : plotTileBox('self', col, p)
    }).filter(Boolean)
    if (!centres.length) return []
    const r = DRONE_RADIUS + PAIR_PAD
    const stroke = PAIR_STROKE, fill = PAIR_FILL
    const out = []
    if (link && centres.length === 2) {
        const [a, b] = centres
        const d = Math.hypot(b.cx - a.cx, b.cy - a.cy) || 1
        const ux = (b.cx - a.cx) / d, uy = (b.cy - a.cy) / d
        if (d > 2 * r) out.push(
            <line key="link" data-pair-link="true" x1={a.cx + ux * r} y1={a.cy + uy * r} x2={b.cx - ux * r} y2={b.cy - uy * r}
                stroke={stroke} strokeWidth={PAIR_RING_W + 1} strokeLinecap="round" />
        )
    }
    centres.forEach((c, i) => out.push(
        <circle key={`ring-${i}`} data-pair-ring={uids[i]} cx={c.cx} cy={c.cy} r={r}
            fill={fill} stroke={stroke} strokeWidth={PAIR_RING_W} />
    ))
    // A hovered mate's ring sends out a slow ripple (.hexb__pair-ripple) that
    // grows past the finger and fades: the pairing has caught, let go.
    if (!link) centres.forEach((c, i) => out.push(
        <circle key={`ripple-${i}`} className="hexb__pair-ripple" data-pair-ripple={uids[i]} cx={c.cx} cy={c.cy} r={r}
            fill="none" stroke={stroke} strokeWidth={PAIR_RING_W} vectorEffect="non-scaling-stroke" />
    ))
    return [
        <g key="pair" className={link ? 'hexb__pair' : 'hexb__pair hexb__pair--mate'} data-pair={col} style={{ pointerEvents: 'none' }}>
            {out}
        </g>,
    ]
}
